import { call } from '@advanza/api'
import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import InputErrorMessage from '@trustoo/components/input/InputErrorMessage'
import InputField from '@trustoo/components/input/InputField'
import TextAreaField from '@trustoo/components/input/TextAreaField'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import Switch from '@trustoo/components/ui/Switch'
import {
    createTempKeywordMappingId,
    h1HelpText,
    INVALIDATE_KEYWORD_MAPPING_SEARCH_INTENTS,
    INVALIDATE_KEYWORD_MAPPINGS,
    readFieldErrors,
    readGenericError,
    withoutField,
} from 'components/keywordMappings/keywordMappingModel'
import KeywordMappingServiceSelect from 'components/keywordMappings/KeywordMappingServiceSelect'
import { useAppDispatch } from 'hooks/hooks'
import { KeywordMapping, KeywordMappingSave } from 'interfaces/KeywordMapping'
import { FormEvent, useState } from 'react'

interface KeywordMappingFormModalProps {
    mapping?: KeywordMapping
    h1Placeholders: string[]
}

const KeywordMappingFormModal = NiceModal.create(
    ({ mapping, h1Placeholders }: KeywordMappingFormModalProps) => {
        const modal = useModal()
        const dispatch = useAppDispatch()
        const [tempId] = useState(createTempKeywordMappingId)
        const [serviceId, setServiceId] = useState<number | null>(mapping?.service_id ?? null)
        const [keyword, setKeyword] = useState(mapping?.keyword ?? '')
        const [searchIntent, setSearchIntent] = useState(mapping?.search_intent ?? '')
        const [h1Text, setH1Text] = useState(mapping?.h1_text ?? '')
        const [active, setActive] = useState(mapping?.active ?? true)
        const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
        const [formError, setFormError] = useState('')
        const [isSaving, setIsSaving] = useState(false)

        const title = mapping === undefined ? 'New keyword mapping' : 'Edit keyword mapping'

        const clearFieldError = (field: string) => {
            setFieldErrors((current) => withoutField(current, field))
        }

        const onSubmit = (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            if (isSaving) return
            if (serviceId === null) {
                setFieldErrors((current) => ({ ...current, service_id: 'Select a service.' }))
                return
            }

            const body: KeywordMappingSave = {
                keyword_mapping_id: mapping?.keyword_mapping_id ?? tempId,
                service_id: serviceId,
                keyword,
                search_intent: searchIntent,
                h1_text: h1Text,
                active,
            }

            setIsSaving(true)
            setFormError('')
            call('office/keyword-mappings/save', { json: [body] })
                .then(() => {
                    dispatch({ type: INVALIDATE_KEYWORD_MAPPINGS })
                    dispatch({ type: INVALIDATE_KEYWORD_MAPPING_SEARCH_INTENTS })
                    modal.hide()
                })
                .catch((response: unknown) => {
                    const errors = readFieldErrors(response)
                    setFieldErrors(errors)
                    if (Object.keys(errors).length === 0) {
                        setFormError(
                            readGenericError(response, 'Could not save the keyword mapping.'),
                        )
                    }
                })
                .finally(() => {
                    setIsSaving(false)
                })
        }

        return (
            <BaseModal preventClose={isSaving} closeButtonLabel="Close">
                <form className="flex min-h-0 w-full flex-1 flex-col" onSubmit={onSubmit}>
                    <BaseModalHeader>
                        <h3 className="m-0">{title}</h3>
                    </BaseModalHeader>
                    <BaseModalMainContent className="flex flex-col gap-4">
                        <div>
                            <KeywordMappingServiceSelect
                                name="service_id"
                                emptyOption="Select a service"
                                disabled={isSaving}
                                error={fieldErrors.service_id}
                                value={serviceId}
                                onChange={(nextServiceId) => {
                                    setServiceId(nextServiceId)
                                    clearFieldError('service_id')
                                }}
                            />
                        </div>
                        <InputField
                            id="keyword-mapping-keyword"
                            name="keyword"
                            legend="Keyword"
                            aria-label="Keyword"
                            value={keyword}
                            disabled={isSaving}
                            error={fieldErrors.keyword}
                            onChange={(event) => {
                                setKeyword(event.target.value)
                                clearFieldError('keyword')
                            }}
                        />
                        <InputField
                            id="keyword-mapping-search-intent"
                            name="search_intent"
                            legend="Search intent"
                            aria-label="Search intent"
                            value={searchIntent}
                            disabled={isSaving}
                            error={fieldErrors.search_intent}
                            onChange={(event) => {
                                setSearchIntent(event.target.value)
                                clearFieldError('search_intent')
                            }}
                        />
                        <div>
                            <TextAreaField
                                id="keyword-mapping-h1"
                                name="h1_text"
                                legend="H1 text"
                                aria-label="H1 text"
                                aria-describedby="keyword-mapping-h1-help"
                                rows={4}
                                value={h1Text}
                                disabled={isSaving}
                                error={fieldErrors.h1_text}
                                onChange={(event) => {
                                    setH1Text(event.target.value)
                                    clearFieldError('h1_text')
                                }}
                            />
                            <p id="keyword-mapping-h1-help" className="mt-1 text-sm text-[#666666]">
                                {h1HelpText(h1Placeholders)}
                            </p>
                        </div>
                        <div>
                            <label className="inline-flex cursor-pointer items-center gap-3">
                                <Switch
                                    checked={active}
                                    disabled={isSaving}
                                    aria-label="Active"
                                    onCheckedChange={(next) => {
                                        setActive(next)
                                        clearFieldError('active')
                                    }}
                                />
                                <span>Active</span>
                            </label>
                            {fieldErrors.active !== undefined && (
                                <InputErrorMessage error={fieldErrors.active} />
                            )}
                        </div>
                        {formError !== '' && <InputErrorMessage error={formError} />}
                    </BaseModalMainContent>
                    <BaseModalFooter className="flex items-center justify-end gap-2">
                        <Button
                            type="button"
                            name="text"
                            onClick={() => modal.hide()}
                            disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button type="submit" appearance="primary" disabled={isSaving}>
                            {isSaving ? 'Saving…' : 'Save'}
                        </Button>
                    </BaseModalFooter>
                </form>
            </BaseModal>
        )
    },
)

export default KeywordMappingFormModal
